// src/components/jnanadeepam/JnanaDeepam.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  FileText,
  HelpCircle,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import { publicAPI } from "../../services/publicApi";
import { useStudentAuth } from "../../student/context/StudentAuthContext";
import StudentAuthModal from "../common/StudentAuthModal";
import {
  jnanaDeepamQuizzes,
  jnanaDeepamMains,
} from "../../data/jnanaDeepamData";
import DailyPrelimsQuiz from "./DailyPrelimsQuiz";
import DailyMainsSecure from "./DailyMainsSecure";

export default function JnanaDeepam({ defaultTab }) {
  const navigate = useNavigate();
  const { isStudentAuthenticated } = useStudentAuth();

  const [activeTab, setActiveTab] = useState(() => {
    if (defaultTab) return defaultTab;
    if (typeof window !== "undefined") {
      const hash = window.location.hash;
      if (hash === "#mains") return "mains";
    }
    return "prelims";
  });

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  // Dynamic published initiatives from MongoDB backend
  const [publishedPrelims, setPublishedPrelims] = useState([]);
  const [publishedMains, setPublishedMains] = useState([]);
  const [loadingPrelims, setLoadingPrelims] = useState(false);

  // Student Auth Modal state
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // callback to run after login

  // Hash navigation sync
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash;
      if (hash === "#mains") setActiveTab("mains");
      else setActiveTab("prelims");
    };
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Fetch Today's Published Prelims Tests (Strict IST match)
  const fetchPrelims = useCallback(async (isBg = false) => {
    if (!isBg) setLoadingPrelims(true);
    try {
      const res = await publicAPI.getPrelimsToday();
      if (res?.success && Array.isArray(res.data)) {
        setPublishedPrelims(res.data);
      }
    } catch (e) {
      console.warn("Could not fetch today's Prelims tests:", e.message);
    } finally {
      if (!isBg) setLoadingPrelims(false);
    }
  }, []);

  // Fetch Published Mains Initiatives
  const fetchMains = useCallback(async () => {
    try {
      const res = await publicAPI.getMainsInitiatives();
      if (res?.success && Array.isArray(res.data)) {
        setPublishedMains(res.data);
      }
    } catch (e) {
      console.warn("Could not fetch published Mains initiatives:", e.message);
    }
  }, []);

  // Auto-synchronization (polling every 12 seconds)
  useEffect(() => {
    fetchPrelims();
    fetchMains();

    const interval = setInterval(() => {
      fetchPrelims(true);
      fetchMains();
    }, 12000);

    const onFocus = () => {
      fetchPrelims(true);
      fetchMains();
    };

    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchPrelims, fetchMains]);

  // Handle clicking "Attend Test" on a Prelims test
  const handleAttendTest = (test) => {
    if (!isStudentAuthenticated) {
      setPendingAction(() => () => {
        navigate(`/student/prelims/test/${test._id}`);
      });
      setIsAuthModalOpen(true);
    } else {
      navigate(`/student/prelims/test/${test._id}`);
    }
  };

  return (
    <div className="w-full bg-slate-50 py-8 px-4 md:px-8 space-y-8 min-h-screen">

      {/* Tabs Filter Bar: Prelims & Mains */}
      <div className="max-w-6xl mx-auto flex justify-center">
        <div className="inline-flex bg-white p-1.5 rounded-2xl shadow-sm border border-slate-200">
          <button
            onClick={() => {
              setActiveTab("prelims");
              window.history.pushState(null, "", "#prelims");
            }}
            className={`px-6 py-2.5 text-sm font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "prelims"
                ? "bg-red-800 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <BookOpen size={16} />
            <span>Prelims Tests ({publishedPrelims.length})</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("mains");
              window.history.pushState(null, "", "#mains");
            }}
            className={`px-6 py-2.5 text-sm font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === "mains"
                ? "bg-red-800 text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <FileText size={16} />
            <span>Mains Initiatives ({publishedMains.length})</span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-12">
        {/* ===================== PRELIMS SECTION ===================== */}
        {activeTab === "prelims" && (
          <section id="prelims" className="scroll-mt-28 space-y-8">
            {/* 1. Dynamic Admin Published Mock Tests */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 pb-4 border-b border-slate-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                      Today's Prelims Mock Tests
                    </h2>
                    <span className="text-xs font-bold text-red-800 bg-red-50 border border-red-200 px-2.5 py-0.5 rounded-full">
                      Today's Test Only (IST)
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Official UPSC Prelims mock tests scheduled for today. Evaluated on the server with automated score and percentile computation.
                  </p>
                </div>
              </div>

              {loadingPrelims ? (
                <div className="py-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                  <RefreshCw size={24} className="animate-spin text-red-800" />
                  <span>Checking today's scheduled Prelims tests...</span>
                </div>
              ) : publishedPrelims.length === 0 ? (
                <div className="py-12 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <HelpCircle className="mx-auto text-slate-400 mb-2" size={32} />
                  <p className="text-sm font-bold text-slate-700">No Prelims Test Scheduled Today</p>
                  <p className="text-xs text-slate-500 mt-1">
                    There are no Prelims tests scheduled for today. When admin publishes a test for today's date, it will appear here automatically.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {publishedPrelims.map((test) => {
                    const totalQ = test.totalQuestions || (test.questions ? test.questions.length : 0);
                    const isUpcoming = test.availability === "Upcoming" || (!test.canStart && test.availability !== "Test Closed");
                    const isClosed = test.availability === "Test Closed" || (!test.canStart && test.availability === "Test Closed");
                    const isAvailable = test.availability === "Available Now" || (test.canStart && !isUpcoming && !isClosed);

                    let badgeClasses = "text-emerald-700 bg-emerald-50 border-emerald-200";
                    let statusLabel = "Available Now";

                    if (isUpcoming) {
                      badgeClasses = "text-amber-800 bg-amber-50 border-amber-200";
                      statusLabel = "Upcoming";
                    } else if (isClosed) {
                      badgeClasses = "text-rose-700 bg-rose-50 border-rose-200";
                      statusLabel = "Test Closed";
                    }

                    return (
                      <div
                        key={test._id}
                        className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-red-800/40 shadow-xs hover:shadow-md transition-all flex flex-col justify-between group"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <span className={`text-[10px] font-bold uppercase tracking-wider border px-2.5 py-0.5 rounded-full ${badgeClasses}`}>
                              ● {statusLabel}
                            </span>
                            <span className="text-[11px] text-slate-500 font-semibold">
                              {test.startTime || "09:00"} – {test.endTime || "21:00"} IST
                            </span>
                          </div>

                          <h3 className="font-bold text-slate-900 group-hover:text-red-900 text-base leading-snug transition-colors mb-1.5">
                            {test.title}
                          </h3>

                          {test.description && (
                            <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-4">
                              {test.description}
                            </p>
                          )}

                          {/* Stats badge grid */}
                          <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-xl p-2.5 text-center mb-4 border border-slate-100">
                            <div>
                              <p className="text-[10px] text-slate-400 font-semibold uppercase">Questions</p>
                              <p className="text-xs font-bold text-slate-800 mt-0.5">{totalQ}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 font-semibold uppercase">Duration</p>
                              <p className="text-xs font-bold text-slate-800 mt-0.5">{test.durationMinutes || 120}m</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-400 font-semibold uppercase">Total Marks</p>
                              <p className="text-xs font-bold text-slate-800 mt-0.5">{test.totalMarks || 200}</p>
                            </div>
                          </div>
                        </div>

                        {/* Start Test Action Button */}
                        {isAvailable ? (
                          <button
                            onClick={() => handleAttendTest(test)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-red-800 hover:bg-red-900 text-white font-bold text-xs shadow-xs hover:shadow-md transition-all active:scale-[0.99] cursor-pointer"
                          >
                            <span>Start Test</span>
                            <ArrowRight size={14} />
                          </button>
                        ) : isUpcoming ? (
                          <button
                            disabled
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 font-bold text-xs cursor-not-allowed"
                          >
                            <span>Upcoming (Starts {test.startTime || "09:00"} IST)</span>
                          </button>
                        ) : (
                          <button
                            disabled
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-100 text-slate-400 border border-slate-200 font-bold text-xs cursor-not-allowed"
                          >
                            <span>Test Closed ({test.endTime || "21:00"} IST)</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 2. Interactive Sectional Practice Drills */}
            <div className="pt-4">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-slate-900">
                  Daily MCQ Practice Drills & Previous Series
                </h3>
                <p className="text-xs text-slate-500">
                  Topic-wise daily quizzes with immediate answers, explanations, and review tools.
                </p>
              </div>
              <DailyPrelimsQuiz quizzes={jnanaDeepamQuizzes} />
            </div>
          </section>
        )}

        {/* ===================== MAINS SECTION ===================== */}
        {activeTab === "mains" && (
          <section id="mains" className="scroll-mt-28">
            <DailyMainsSecure
              questions={jnanaDeepamMains}
              backendMains={publishedMains}
            />
          </section>
        )}
      </div>


      {/* Student Auth Modal */}
      <StudentAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setPendingAction(null);
        }}
        onSuccess={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        targetActionName="this UPSC test initiative"
      />
    </div>
  );
}
